import React, { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Background } from "@/components/Background";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { LeaderCard } from "@/components/LeaderCard";
import { CoinCard } from "@/components/CoinCard";
import { CommunityActivity } from "@/components/CommunityActivity";
import { HowItWorks } from "@/components/HowItWorks";
import { Footer } from "@/components/Footer";
import { fetchState, castVote, forceShuffle, fmtCountdown } from "@/lib/api";

function App() {
  const [state, setState] = useState(null);
  const [shuffleSecs, setShuffleSecs] = useState(0);
  const [leaderSecs, setLeaderSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const roundIdRef = useRef(null);

  const applyState = useCallback((data) => {
    if (data.round.id !== roundIdRef.current && roundIdRef.current !== null) {
      toast("🔀 Trending shuffled — 4 fresh coins are in play!", {
        style: { background: "#0c0f17", color: "#fff", border: "1px solid rgba(0,240,255,0.3)" },
      });
    }
    roundIdRef.current = data.round.id;
    setState(data);
    setShuffleSecs(data.round.shuffle_seconds_left);
    setLeaderSecs(data.leader ? data.leader.seconds_left : 0);
  }, []);

  const load = useCallback(async () => {
    try {
      applyState(await fetchState());
    } catch (e) {
      console.error("state load failed", e);
    }
  }, [applyState]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 7000);
    return () => clearInterval(poll);
  }, [load]);

  // local per-second ticker
  useEffect(() => {
    const t = setInterval(() => {
      setShuffleSecs((s) => {
        if (s <= 1) {
          load();
          return 0;
        }
        return s - 1;
      });
      setLeaderSecs((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [load]);

  const onVote = useCallback(
    async (coin) => {
      try {
        const data = await castVote(coin.id);
        applyState(data);
        toast.success(`🚀 Sent it for $${coin.symbol}!`, {
          description: "Your vote is in. Votes updated live.",
          style: { background: "#0c0f17", color: "#fff", border: "1px solid rgba(0,255,102,0.35)" },
        });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Vote failed";
        toast.error(msg, {
          style: { background: "#0c0f17", color: "#fff", border: "1px solid rgba(255,0,122,0.35)" },
        });
        load();
      }
    },
    [applyState, load]
  );

  const onShuffle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      applyState(await forceShuffle());
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }, [applyState, busy]);

  const onNav = useCallback((id) => {
    if (id === "terms" || id === "privacy") {
      toast(`${id === "terms" ? "Terms" : "Privacy"}: SEND IT is a community meme experiment. Not financial advice.`, {
        style: { background: "#0c0f17", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" },
      });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const jumpToVote = () =>
    document.getElementById("trending")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="App min-h-screen relative">
      <Background />
      <Toaster position="top-center" />
      <Navbar shuffleLabel={fmtCountdown(shuffleSecs)} onNav={onNav} />

      {state ? (
        <>
          <Hero
            shuffleSecs={shuffleSecs}
            totalVotes={state.community.total_votes}
            onVoteJump={jumpToVote}
            onShuffle={onShuffle}
          />

          <main id="trending" className="px-4 sm:px-6 lg:px-8 scroll-mt-20">
            <div className="max-w-7xl mx-auto">
              {state.leader && (
                <div className="mb-10">
                  <LeaderCard leader={{ ...state.leader, seconds_left: leaderSecs }} />
                </div>
              )}

              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl">
                  Trending Now
                </h2>
                <span className="font-mono-num text-sm text-slate-400 hidden sm:block">
                  Top 4 · last 24h
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <AnimatePresence mode="popLayout">
                  {state.round.coins.map((coin, i) => (
                    <CoinCard
                      key={coin.id}
                      coin={coin}
                      rank={i}
                      hasVoted={!!state.my_vote}
                      votedThis={state.my_vote === coin.id}
                      onVote={onVote}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </main>

          <CommunityActivity
            community={state.community}
            recent={state.recent_votes}
            shuffleSecs={shuffleSecs}
          />
          <HowItWorks />
          <Footer onNav={onNav} />
        </>
      ) : (
        <div className="min-h-screen grid place-items-center">
          <div className="font-display font-black text-3xl animate-pulse text-glow-volt text-[#00ff66]">
            LOADING SEND IT…
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
